/*
 * Copyright (c) 2024-2025. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React from "react";
import {useTranslation} from "react-i18next";
import {currencyNumberFormat, percentageTaxFormat, translate} from "utils/utility";
import Loader from "components/Loader";
import {useTypedSelector} from "helpers/reducers";
import CardComponent from "pages/Front/Booking/components/CardComponent";
import {PackagesCartItem} from "types/entities/PackagesCartItem";
import {DEFAULT_HALL, DEFAULT_SHOW_PER_PERSON_PRICE, TAX} from "helpers/config";
import {PackageCategory} from "types/enums/PackageCategory";
import {PackageItemItemType} from "types/entities/PackageItem";
import {HallCode} from "types/enums/HallCode";
import CardLabel from "pages/Front/Booking/components/BookingCustomer/CardLabel";
import DivideLine from "pages/Front/Booking/components/BookingCustomer/DivideLine";

const BookingOverview = () => {
    const {t} = useTranslation();
    const selectedPackage = useTypedSelector(state => state.bookingState.bookingProcess.package);
    const bookingFilter = useTypedSelector(state => state.bookingState.bookingProcess.filter);
    const packagesCart = useTypedSelector(state => state.bookingState.bookingProcess.packageCart);
    const selectedCategory: PackageCategory = (selectedPackage?.category as PackageCategory) || bookingFilter.category;

    if ((!selectedPackage && !selectedCategory) || !packagesCart) {
        return <Loader />;
    }
    if (!packagesCart.prices) {
        return null;
    }
    const prices = packagesCart.prices;
    const persons_count = packagesCart.drivers_count_adult + packagesCart.drivers_count_child;
    const price_taxes = prices.taxes;
    const subtotal_price = prices.subtotal;
    const total_base_price = prices.total_base;
    const discount_price = prices.discount;
    const total_price = prices.total;
    const total_price_per_person = total_price / persons_count;
    const corona = false;
    return (
        <div className={"BookingOverview flex flex-col space-y-8 w-full justify-between"}>
            {corona && (
                <CardComponent>
                    <h3 className={"mb-4 font-bold"}>{t("Coronavirus measures")}</h3>
                    <span className={"text-sm"}>
                        {t(
                            "In case of a government-imposed lockdown, Kobalt Zwolle will be temporarily closed. Already purchased tickets for activities remain valid and can be used at a later time when Kobalt is open again. No refunds will be given."
                        )}
                    </span>
                </CardComponent>
            )}
            <CardComponent>
                <div className={"BookingOverviewCart w-full flex flex-col justify-center items-start rounded-lg"}>
                    <CardLabel title={t("Your order")} />
                    {selectedCategory === PackageCategory.Event ? (
                        <div className={"grid grid-cols-[1fr,0.2fr] text-sm leading-4 w-full font-medium text-gray-900"}>
                            <span>{t("Article")}</span>
                            <span className={"text-right"}>{t("Total")}</span>
                        </div>
                    ) : (
                        <div className={"grid grid-cols-2 md:grid-cols-[1fr,40px,0.5fr,0.5fr] space-x-2 text-xs sm:text-sm leading-4 w-full font-bold text-gray-900 "}>
                            <span>{t("Article")}</span>
                            <span className={"text-right hidden md:block"}>{t("Count")}</span>
                            <span className={"text-right hidden md:block"}>{t("per pers.")}</span>
                            <span className={"text-right"}>{t("Total")}</span>
                        </div>
                    )}

                    <DivideLine />
                    {selectedCategory === PackageCategory.Event && (
                        <div className={"BookingOverviewPackages  text-sm leading-4 font-medium w-full"}>
                            <div className={"grid grid-cols-[1fr,0.2fr] w-full"}>
                                <div className={"text-left w-full font-bold text-lg"}>
                                    {/*{translate(selectedPackage.name)}*/}
                                    {t("Event package")}
                                </div>
                                <div className={"text-right w-full font-bold text-lg"}>{currencyNumberFormat(total_base_price)}</div>
                            </div>
                        </div>
                    )}

                    <div className={"BookingOverviewPackagesList w-full"}>
                        {selectedCategory !== PackageCategory.Event &&
                            packagesCart.packagesCartItems &&
                            packagesCart.packagesCartItems.map((packageCartItem: PackagesCartItem, index: number) => {
                                const isAdult = packageCartItem && packageCartItem.drivers_count_adult && packageCartItem.drivers_count_adult > 0;
                                const driversCount = isAdult ? packageCartItem.drivers_count_adult : packageCartItem.drivers_count_child;
                                const packageItem = packageCartItem?.packageItem;

                                if (
                                    !packageItem ||
                                    !packageItem.name ||
                                    !packageCartItem ||
                                    packageItem.type !== PackageItemItemType.PackageItem
                                ) {
                                    return false;
                                }
                                return (
                                    <div
                                        key={index}
                                        className={
                                            "BookingOverviewPackages text-xs sm:text-sm leading-4 w-full grid grid-cols-3 md:grid-cols-[1fr,40px,0.5fr,0.5fr] py-1.5 border-b border-dashed last-of-type:border-0 gap-0.5 items-center"
                                        }
                                    >
                                        <span className={"col-span-3 md:col-span-1"}>
                                            {translate(packageItem.name)}{" "}
                                            {bookingFilter &&
                                                (bookingFilter.category === PackageCategory.Action ||
                                                    bookingFilter.category === PackageCategory.School ||
                                                    bookingFilter.category === PackageCategory.FerienPass) && (
                                                    <span>- {isAdult ? t("adult") : t("child")}</span>
                                                )}
                                        </span>
                                        <span className={"text-left md:text-right col-span-1 self-end"}>{driversCount}x</span>
                                        <span className={"text-right col-span-1 self-end"}>{driversCount !== 0 && currencyNumberFormat(packageCartItem.price / driversCount)}</span>
                                        <span className={"text-right col-span-1 font-medium self-end"}>{currencyNumberFormat(packageCartItem.price)}</span>
                                    </div>
                                );
                            })}
                    </div>
                    {packagesCart.packagesCartItems && packagesCart.packagesCartItems.length > 0 && (
                        <div className={"BookingUpsellsList w-full"}>
                            {packagesCart.packagesCartItems.map((packageCartItem: PackagesCartItem, index: number) => {
                                const isAdult = packageCartItem && packageCartItem.drivers_count_adult && packageCartItem.drivers_count_adult > 0;
                                const packageItem = packageCartItem?.packageItem;
                                const driversCount = isAdult ? packageCartItem.drivers_count_adult : packageCartItem.drivers_count_child;

                                if (
                                    !packageItem ||
                                    !packageItem.name ||
                                    !packageCartItem ||
                                    packageItem.type !== PackageItemItemType.Upsell
                                ) {
                                    return false;
                                }

                                return (
                                    <div
                                        key={index}
                                        className={"BookingUpsells text-xs sm:text-sm leading-4 w-full grid grid-cols-3 md:grid-cols-[1fr,40px,0.5fr,0.5fr] py-1.5 border-b border-dashed last-of-type:border-b-0 gap-0.5 items-center first-of-type:border-t"}
                                    >
                                        <div className={"flex flex-col col-span-3 md:col-span-1"}>
                                            {packageItem.is_variant && packageItem.packageItemVariant ? (
                                                <span className={"uppercase font-bold text-[10px] sm:text-xs"}>{translate(packageItem.packageItemVariant?.name)}</span>
                                            ) : (
                                                ""
                                            )}
                                            <span>{translate(packageItem.name)}</span>
                                            {/*- {isAdult ? t("adult") : t("child")}{" "}*/}
                                        </div>
                                        <div className={"text-left md:text-right col-span-1 self-end"}>{driversCount}x</div>
                                        <span className={"text-right col-span-1 self-end"}>{driversCount !== 0 && currencyNumberFormat(packageCartItem.price / driversCount)}</span>
                                        <span className={"text-right col-span-1 self-end font-medium"}>{currencyNumberFormat(packageCartItem.price)}</span>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
                <DivideLine />
                <div className={"BookingOverviewTotals grid grid-cols-2 mt-1 space-y-2 text-sm leading-4 font-medium justify-between items-center w-full"}>
                    {DEFAULT_HALL !== HallCode.Winterthur && DEFAULT_HALL !== HallCode.Spreitenbach && (
                        <>
                            <div className={"text-left w-full"}>{t("Subtotal")}</div>
                            <div className={"text-right w-full"}>{currencyNumberFormat(subtotal_price)}</div>
                        </>
                    )}
                    <div className={"text-left w-full"}>{t("Discount")}</div>
                    <div className={"text-right w-full"}>
                        {discount_price > 0 && "-"} {currencyNumberFormat(discount_price)}
                    </div>
                    <div className={"text-left w-full"}>
                        {t("Tax")}&nbsp;{percentageTaxFormat(TAX)}
                    </div>
                    <div className={"text-right w-full"}>{currencyNumberFormat(price_taxes)}</div>
                    <div className={"col-span-2 mt-2"} />
                    <div className={"text-left w-full font-bold text-lg"}>{t("Total")}</div>
                    <div className={"text-right w-full font-bold text-lg"}>{currencyNumberFormat(total_price)}</div>
                    <div className={"text-left w-full"} />
                    {DEFAULT_SHOW_PER_PERSON_PRICE && <div className={"text-right w-full italic"}>(pp. {currencyNumberFormat(total_price_per_person)})</div>}
                </div>
            </CardComponent>
        </div>
    );
};

export default BookingOverview;
